import React, { useRef, useState, useEffect, useCallback, Suspense } from 'react';
import { X, Minimize, Square, Plus, Move, ArrowUpRight, ArrowDownLeft, ArrowUpLeft, ArrowDownRight, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWindowManager, WindowState } from '@/contexts/WindowManager';
import { useResponsive } from '@/hooks/use-responsive';
import { PreviewGenerator } from '@/utils/PreviewGenerator';
import { Skeleton } from '@/components/ui/skeleton';

interface WindowProps {
  window: WindowState;
}

export const Window: React.FC<WindowProps> = ({ window }) => {
  const { focusWindow, closeWindow, minimizeWindow, maximizeWindow, moveWindow, resizeWindow, isMobile, isTablet } = useWindowManager();
  const { width: screenWidth, height: screenHeight } = useResponsive();
  const windowRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Find ALL scrollable elements inside this window and save/restore their scroll
  const scrollMapRef = useRef<Map<Element, number>>(new Map());

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const findScrollableElements = (): Element[] => {
      const scrollables: Element[] = [];
      const walk = (el: Element) => {
        if (el.scrollHeight > el.clientHeight + 1) {
          scrollables.push(el);
        }
        for (const child of Array.from(el.children)) walk(child);
      };
      walk(container);
      return scrollables;
    };

    if (!window.isActive) {
      // BLUR — save all scroll positions
      const map = new Map<Element, number>();
      for (const el of findScrollableElements()) {
        if (el.scrollTop > 0) map.set(el, el.scrollTop);
      }
      scrollMapRef.current = map;
    } else {
      // FOCUS — restore all scroll positions
      requestAnimationFrame(() => {
        for (const [el, top] of scrollMapRef.current) {
          if (el.isConnected) el.scrollTop = top;
        }
      });
    }
  }, [window.isActive]);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Enhanced resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string>('');
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  // Local visual state for smooth resizing
  const [localSize, setLocalSize] = useState({ width: 0, height: 0 });
  const [localPosition, setLocalPosition] = useState({ x: 0, y: 0 });
  const resizeAnimationRef = useRef<number | null>(null);

  // Initialize local state
  useEffect(() => {
    setLocalSize({ width: window.size.width, height: window.size.height });
    setLocalPosition({ x: window.position.x, y: window.position.y });
  }, [window.size, window.position]);

  // Smooth resize handler using requestAnimationFrame
  const updateResize = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    
    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newX = window.position.x;
    let newY = window.position.y;

    // Calculate new dimensions based on resize direction
    if (resizeDirection.includes('e')) {
      newWidth = Math.max(320, Math.min(globalThis.innerWidth - newX, resizeStart.width + deltaX));
    }
    if (resizeDirection.includes('s')) {
      newHeight = Math.max(200, Math.min(globalThis.innerHeight - newY, resizeStart.height + deltaY));
    }
    if (resizeDirection.includes('w')) {
      const minX = Math.max(0, resizeStart.x - resizeStart.width + 320);
      newX = Math.max(minX, Math.min(resizeStart.x, window.position.x + deltaX));
      newWidth = Math.max(320, resizeStart.width - (newX - window.position.x));
    }
    if (resizeDirection.includes('n')) {
      const minY = Math.max(0, resizeStart.y - resizeStart.height + 200);
      newY = Math.max(minY, Math.min(resizeStart.y, window.position.y + deltaY));
      newHeight = Math.max(200, resizeStart.height - (newY - window.position.y));
    }

    // Update local state immediately for smooth visual feedback
    setLocalSize({ width: newWidth, height: newHeight });
    setLocalPosition({ x: newX, y: newY });
  }, [isResizing, resizeDirection, resizeStart, window]);

  useEffect(() => {
    const MENU_BAR_HEIGHT = 28; // Height of the OS menu bar (h-7 = 28px)
    const SNAP_THRESHOLD = 10; // Pixels from top to trigger snap
    
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        const newX = Math.max(0, Math.min(globalThis.innerWidth - window.size.width, window.position.x + deltaX));
        const potentialY = window.position.y + deltaY;
        
        // Constrain window to stay below menu bar
        const newY = Math.max(MENU_BAR_HEIGHT, Math.min(globalThis.innerHeight - window.size.height, potentialY));
        
        // Check if dragging to top edge for snap-to-fullscreen
        if (e.clientY <= SNAP_THRESHOLD && !window.isMaximized) {
          // Show visual feedback - add subtle highlight to indicate snap zone
          document.body.style.setProperty('--snap-active', '1');
        } else {
          document.body.style.removeProperty('--snap-active');
        }
        
        // Smooth dragging with immediate local state update
        setLocalPosition({ x: newX, y: newY });
        setDragStart({ x: e.clientX, y: e.clientY });
        
        // Throttle global state updates for performance
        requestAnimationFrame(() => {
          moveWindow(window.id, { x: newX, y: newY });
        });
      }

      if (isResizing) {
        if (resizeAnimationRef.current) {
          cancelAnimationFrame(resizeAnimationRef.current);
        }
        resizeAnimationRef.current = requestAnimationFrame(() => updateResize(e));
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isDragging) {
        const SNAP_THRESHOLD = 10;
        
        // Check if released at top edge - snap to fullscreen
        if (e.clientY <= SNAP_THRESHOLD && !window.isMaximized) {
          maximizeWindow(window.id);
        }
        
        setIsDragging(false);
        document.body.style.removeProperty('--snap-active');
        
        // Ensure final position is synced
        moveWindow(window.id, localPosition);
      }
      
      if (isResizing) {
        setIsResizing(false);
        setResizeDirection('');
        // Sync final size and position to global state
        resizeWindow(window.id, localSize);
        if (localPosition.x !== window.position.x || localPosition.y !== window.position.y) {
          moveWindow(window.id, localPosition);
        }
      }
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Dynamic cursor based on resize direction
      let cursor = 'move';
      if (isResizing) {
        if (resizeDirection === 'se' || resizeDirection === 'nw') cursor = 'nw-resize';
        else if (resizeDirection === 'sw' || resizeDirection === 'ne') cursor = 'ne-resize';
        else if (resizeDirection === 'n' || resizeDirection === 's') cursor = 'ns-resize';
        else if (resizeDirection === 'e' || resizeDirection === 'w') cursor = 'ew-resize';
      }
      
      document.body.style.cursor = cursor;
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (resizeAnimationRef.current) {
        cancelAnimationFrame(resizeAnimationRef.current);
      }
    };
  }, [isDragging, isResizing, dragStart, resizeDirection, window, moveWindow, resizeWindow, updateResize, localPosition, localSize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.window-title-bar')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
    focusWindow(window.id);
  };

  const handleResizeMouseDown = (direction: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    setResizeStart({ 
      x: e.clientX, 
      y: e.clientY, 
      width: window.size.width, 
      height: window.size.height 
    });
  };

  // Handle minimize with thumbnail capture
  const handleMinimize = useCallback(async () => {
    if (contentRef.current) {
      try {
        const thumbnail = await PreviewGenerator.generateWindowThumbnail(
          contentRef.current,
          window.id,
          { width: 80, height: 60, quality: 0.7 }
        );
        minimizeWindow(window.id, thumbnail || undefined);
      } catch (error) {
        console.warn('Failed to capture window thumbnail:', error);
        minimizeWindow(window.id);
      }
    } else {
      minimizeWindow(window.id);
    }
  }, [window.id, minimizeWindow]);

  if (window.isMinimized) {
    return null;
  }

  const MENU_BAR_HEIGHT = 28; // Match actual menu bar height (h-7)
  
  const windowStyle = window.isMaximized || isMobile
    ? { 
        left: 0, 
        top: MENU_BAR_HEIGHT, // Leave space for menu bar
        width: '100vw', 
        height: `calc(100vh - ${MENU_BAR_HEIGHT}px)`, // Subtract menu bar height
        borderRadius: 0
      }
    : {
        left: Math.max(0, Math.min(screenWidth - (isResizing ? localSize.width : window.size.width), 
                                    isResizing ? localPosition.x : window.position.x)),
        top: Math.max(MENU_BAR_HEIGHT, Math.min(screenHeight - (isResizing ? localSize.height : window.size.height), 
                                               isResizing ? localPosition.y : window.position.y)),
        width: Math.min(screenWidth, isResizing ? localSize.width : window.size.width),
        height: Math.min(screenHeight - MENU_BAR_HEIGHT, isResizing ? localSize.height : window.size.height),
      };

  return (
    <div
      ref={windowRef}
      className={cn(
        "fixed overflow-hidden",
        // Disable transitions during active resize/drag for smooth performance
        (isDragging || isResizing) ? "transition-none" : "transition-[box-shadow,border-color,opacity] duration-200",
        // Add GPU acceleration hint
        "will-change-transform",
        // Maximized/Mobile: pure glass with lighter blur
        (window.isMaximized || isMobile)
          ? "backdrop-blur-lg bg-white/5 border-none shadow-none"
          : [
              // Normal windowed mode: glass with active/inactive differentiation
              "backdrop-blur-lg rounded-xl",
              // Active window styling
              window.isActive 
                ? "shadow-[0_20px_40px_rgba(0,0,0,0.2)] border border-white/15 bg-white/3"
                : "shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-white/10 bg-white/2"
            ],
        // Mobile-specific styles
        isMobile && "!rounded-none !border-none"
      )}
      style={{
        ...windowStyle,
        zIndex: Math.min(window.zIndex, 9999), // Ensure windows stay below menu bar (z-index 10000)
      }}
      onMouseDown={handleMouseDown}
    >
      {/* macOS-style Title Bar */}
      <div 
        className={cn(
          "window-title-bar flex items-center justify-between h-10 px-4 cursor-move",
          // Add GPU acceleration for smooth dragging
          "gpu-accelerate",
          // Reduced glass effect for title bar to minimize GPU load
          window.isMaximized 
            ? "backdrop-blur-sm bg-white/5 border-b border-white/8" // Maximized: lighter blur
            : "backdrop-blur-sm bg-white/8 border-b border-white/15 rounded-t-xl" // Normal: lighter blur
        )}
        onDoubleClick={() => maximizeWindow(window.id)}
      >
        <span className={cn(
          "text-sm font-medium truncate",
          window.isActive ? "text-white" : "text-white/70"
        )}>
          {window.title}
        </span>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMinimize();
            }}
            className="w-3 h-3 rounded-full bg-amber-500 hover:bg-amber-400 transition-colors shadow-sm border border-amber-600/30 flex items-center justify-center group"
            title="Minimize"
          >
            <Minimize className="w-2 h-2 text-amber-900 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              maximizeWindow(window.id);
            }}
            className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-400 transition-colors shadow-sm border border-green-600/30 flex items-center justify-center group"
            title={window.isMaximized ? "Restore" : "Maximize"}
          >
            {window.isMaximized ? (
              <Square className="w-2 h-2 text-green-900 opacity-0 group-hover:opacity-100 transition-opacity" />
            ) : (
              <Plus className="w-2 h-2 text-green-900 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeWindow(window.id);
            }}
            className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-400 transition-colors shadow-sm border border-red-600/30 flex items-center justify-center group"
            title="Close"
          >
            <X className="w-2 h-2 text-red-900 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div 
        ref={contentRef}
        className={cn(
          "flex-1 overflow-hidden",
          window.isMaximized ? "h-[calc(100vh-68px)]" : "h-[calc(100%-2.5rem)]" // 28px menu bar + 40px title bar = 68px
        )}
      >
        <div className="h-full overflow-auto">
          <Suspense
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
            }
          >
            {window.component}
          </Suspense>
        </div>
      </div>

      {/* Multiple Resize Handles - Only show for non-maximized desktop windows */}
      {!window.isMaximized && !isMobile && (
        <>
          {/* Corner handles */}
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize group flex items-center justify-center"
            onMouseDown={handleResizeMouseDown('se')}
          >
            <ArrowDownRight className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          
          <div
            className="absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize group flex items-center justify-center"
            onMouseDown={handleResizeMouseDown('sw')}
          >
            <ArrowDownLeft className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          <div
            className="absolute top-0 right-0 w-4 h-4 cursor-ne-resize group flex items-center justify-center"
            onMouseDown={handleResizeMouseDown('ne')}
          >
            <ArrowUpRight className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          <div
            className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize group flex items-center justify-center"
            onMouseDown={handleResizeMouseDown('nw')}
          >
            <ArrowUpLeft className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          {/* Edge handles */}
          <div
            className="absolute bottom-0 left-4 right-4 h-2 cursor-s-resize group flex items-center justify-center"
            onMouseDown={handleResizeMouseDown('s')}
          >
            <ArrowDown className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          
          <div
            className="absolute top-0 left-4 right-4 h-2 cursor-n-resize group flex items-center justify-center"
            onMouseDown={handleResizeMouseDown('n')}
          >
            <ArrowUp className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          
          <div
            className="absolute right-0 top-4 bottom-4 w-2 cursor-e-resize group flex items-center justify-center"
            onMouseDown={handleResizeMouseDown('e')}
          >
            <ArrowRight className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity rotate-90" />
          </div>
          
          <div
            className="absolute left-0 top-4 bottom-4 w-2 cursor-w-resize group flex items-center justify-center" 
            onMouseDown={handleResizeMouseDown('w')}
          >
            <ArrowLeft className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity rotate-90" />
          </div>
        </>
      )}
    </div>
  );
};